import { useCallback, useRef, useState } from 'react';
import type { ReportDefinition, ReportDetail } from '@tallyframe/core';
import { ApiError } from '../api/errors';
import type { ReportEditing } from '../editing/useReportEditing';

/** What is on screen while previewing an earlier version (spec §10.2). */
export interface VersionPreview {
  revision: number;
  createdAt: string;
  definition: ReportDefinition;
}

export interface UseVersionPreviewOptions {
  reportId: string;
  editing: ReportEditing;
  loadRevision(reportId: string, revision: number): Promise<{ definition: ReportDefinition }>;
  restore(reportId: string, revision: number, expectedRevision: number): Promise<ReportDetail>;
  /** Reads the latest detail; the catch-up after a restore conflict. */
  reload(reportId: string): Promise<ReportDetail>;
  onDetail(detail: ReportDetail): void;
}

export interface VersionPreviewState {
  preview: VersionPreview | null;
  previewError: string | null;
  restoreNotice: string | null;
  restoring: boolean;
  canRestore: boolean;
  open(revision: number, createdAt: string): Promise<void>;
  backToCurrent(): void;
  /** Invalidates any fetch in flight, as closing History does. */
  cancelPending(): void;
  /** Clears the notice under the preview banner, as asking to restore again does. */
  clearRestoreNotice(): void;
  confirmRestore(): Promise<void>;
}

/** Shown after a restore hits a 409 mid-preview: the person's own edits never
 *  caused this (the canvas was read-only), so it names what actually
 *  happened — someone else changed the report while this older version sat
 *  on screen — rather than talking about revisions or ids. The catch-up
 *  read also hands the fresh detail to `onDetail`, which bumps the History
 *  drawer's `changeCounter`, so "the list" really is fresh by the time this
 *  is on screen. */
const RESTORE_CONFLICT_COPY =
  'This report changed while you were looking at an older version. The list has been updated.';

/** Shown when the catch-up read that follows a restore conflict itself
 *  fails: nothing was actually updated, so this says so rather than
 *  RESTORE_CONFLICT_COPY's claim that the list is now fresh. */
const RESTORE_CATCHUP_FAILED_COPY =
  'This report changed while you were looking at an older version. Reload to see the latest.';

/** Shown, on a surface that renders whether or not a preview is on screen,
 *  when fetching a version to preview fails outright. */
const PREVIEW_LOAD_FAILED_COPY = "That version couldn't be loaded.";

/** Plain copy for a failed restore, keyed by the server's error code. Never
 *  the server's own `message` (spec wording rule): none of these mention
 *  "revision" or "snapshot". */
function restoreErrorCopy(cause: unknown): string {
  if (ApiError.isApiError(cause)) {
    switch (cause.code) {
      case 'NO_CHANGE':
        return 'This version is the same as the current one.';
      case 'NOT_FOUND':
        return 'That version is no longer available.';
      case 'REPORT_DEFINITION_INVALID':
        return "That version can't be restored because it no longer fits this report's data.";
      default:
        return 'That version could not be restored.';
    }
  }
  return 'That version could not be restored.';
}

/**
 * The version preview and restore state machine behind the History drawer.
 *
 * Two refs defeat stale closures. `requestRef` identifies the latest request
 * to open a preview: a fetch still in flight when the person moves on — opens
 * another entry, backs out, closes the drawer — sees it changed and drops its
 * result or error. `previewRef` mirrors `preview` in lockstep with every
 * write, so a restore whose POST settles after its preview was closed or
 * replaced can tell, and leaves whatever is on screen now alone.
 */
export function useVersionPreview(options: UseVersionPreviewOptions): VersionPreviewState {
  const { reportId, editing, loadRevision, restore, reload, onDetail } = options;
  const [preview, setPreviewState] = useState<VersionPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  const previewRef = useRef<VersionPreview | null>(null);
  const setPreview = useCallback((next: VersionPreview | null) => {
    previewRef.current = next;
    setPreviewState(next);
  }, []);

  const requestRef = useRef(0);

  // Live, not captured once: a save queued before preview was requested can
  // still be settling when `open` calls `flush()` — that call is what makes
  // the preview wait for it, so by the time the banner is on screen this is
  // normally already true. Nothing can start a *new* save while the preview
  // is showing: the screen disables the title field and Undo/Redo meanwhile.
  const canRestore = editing.saveState === 'saved' && !editing.hasUnsavedWork;

  const open = useCallback(
    async (revision: number, createdAt: string) => {
      const requestId = ++requestRef.current;
      try {
        // Nothing outstanding is left to conflict with the restore, and the
        // version fetched next is read against a settled baseline.
        await editing.flush();
        const record = await loadRevision(reportId, revision);
        if (requestRef.current !== requestId) return; // superseded — drop it
        setRestoreNotice(null);
        setPreviewError(null);
        setPreview({ revision, createdAt, definition: record.definition });
      } catch {
        if (requestRef.current !== requestId) return; // superseded — drop this too
        setPreviewError(PREVIEW_LOAD_FAILED_COPY);
      }
    },
    [editing, loadRevision, reportId, setPreview]
  );

  const cancelPending = useCallback(() => {
    requestRef.current += 1;
    setPreviewError(null);
  }, []);

  const backToCurrent = useCallback(() => {
    // A fetch still in flight must not land later and reopen the banner this closed.
    requestRef.current += 1;
    setPreview(null);
    setRestoreNotice(null);
    setPreviewError(null);
  }, [setPreview]);

  const clearRestoreNotice = useCallback(() => setRestoreNotice(null), []);

  const confirmRestore = useCallback(async () => {
    // Identifies the preview session this restore belongs to; compared with
    // `previewRef` (read fresh, after each await) to know whether it is still
    // the one on screen. `requestRef` cannot answer that: it tracks fetches.
    const restoringPreview = previewRef.current;
    if (!restoringPreview) return;
    setRestoring(true);
    try {
      // Read now, not when preview opened: an edit saved in between moved
      // the baseline this restore has to be conditioned on.
      const restored = await restore(reportId, restoringPreview.revision, editing.revision);
      editing.adoptRestored(restored);
      if (previewRef.current === restoringPreview) {
        setPreview(null);
        setRestoreNotice(null);
      }
    } catch (cause) {
      if (ApiError.isApiError(cause) && cause.code === 'REVISION_CONFLICT') {
        // Somebody else's write landed while this older version sat on
        // screen. Catch up to it without discarding the preview or putting
        // the editor into its own conflict state: nothing here came from an
        // unsent local edit, so autosave has nothing to reconcile.
        try {
          const fresh = await reload(reportId);
          onDetail(fresh);
          editing.adopt(fresh);
          if (previewRef.current === restoringPreview) setRestoreNotice(RESTORE_CONFLICT_COPY);
        } catch {
          // Nothing was actually updated, so say so rather than claim the list is fresh.
          if (previewRef.current === restoringPreview) setRestoreNotice(RESTORE_CATCHUP_FAILED_COPY);
        }
      } else if (previewRef.current === restoringPreview) {
        setRestoreNotice(restoreErrorCopy(cause));
      }
    } finally {
      setRestoring(false);
    }
  }, [editing, onDetail, reload, reportId, restore, setPreview]);

  return {
    preview,
    previewError,
    restoreNotice,
    restoring,
    canRestore,
    open,
    backToCurrent,
    cancelPending,
    clearRestoreNotice,
    confirmRestore
  };
}
